import { useCallback, useMemo } from 'react';

function getTimezoneOffsetHours(timezone: string, date: Date): number {
	const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
	const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
	return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
}

export default function useTimezoneOffset(
	eventTimezone: string | undefined | null,
	eventDate: string | Date,
) {
	const offsetDiff = useMemo(() => {
		if (!eventTimezone) return 0;

		const date = eventDate instanceof Date ? eventDate : new Date(eventDate);
		const viewerOffset = -date.getTimezoneOffset() / 60;
		const eventOffset = getTimezoneOffsetHours(eventTimezone, date);
		return viewerOffset - eventOffset;
	}, [eventTimezone, eventDate]);

	const cityName = useMemo(
		() => eventTimezone?.split('/').pop()?.replace(/_/g, ' ') ?? null,
		[eventTimezone],
	);

	const formatInEventTimezone = useCallback(
		(date: string | Date, format: 'time' | 'datetime' = 'time') => {
			if (!eventTimezone) return null;

			const parsed = date instanceof Date ? date : new Date(date);

			const options: Intl.DateTimeFormatOptions =
				format === 'datetime'
					? {
							timeZone: eventTimezone,
							day: '2-digit',
							month: '2-digit',
							year: 'numeric',
							hour: '2-digit',
							minute: '2-digit',
							hour12: false,
						}
					: {
							timeZone: eventTimezone,
							hour: '2-digit',
							minute: '2-digit',
							hour12: false,
						};

			return new Intl.DateTimeFormat('nl-NL', options).format(parsed);
		},
		[eventTimezone],
	);

	const hasDifference = offsetDiff !== 0;

	return {
		offsetDiff,
		hasDifference,
		cityName,
		formatInEventTimezone,
	};
}
